import { useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowBack, ArrowForward } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, AlertTitle, Box, Paper, PaperProps, Stack, Typography } from '@mui/material';
import { Control, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useBeforeUnload } from 'react-use';
import * as Yup from 'yup';
import { Customer, RemindingTone, useUserQuery } from '../../../../api/gql';
import { FormInput } from '../../../../components/form/FormInput';
import { FormSelect } from '../../../../components/form/FormSelect';
import { PageHeader } from '../../../../components/PageHeader';
import { useXmlDataStore } from '../../../../hooks/useXmlDataStore';
import { useXmlProcessController } from '../../../../hooks/useXmlProcessController';
import { PageContent, PageLayout } from '../../../../layout';
import { getPriceWithCurrencyString } from '../../../../utils/currencies';
import { getRemindingToneTransKey } from '../../../../utils/invoice';
import { fromCentesimal } from '../../../../utils/transform';
import { emailSchema, phoneNumberSchema } from '../../../../utils/validationSchemas';

type FormValues = {
  customers:
    | {
        id: string;
        name: string;
        email?: string;
        phoneNumber?: string;
        remindingTone: RemindingTone;
      }[]
    | undefined;
};

const customersValidationSchema: Yup.ObjectSchema<FormValues> = Yup.object().shape({
  customers: Yup.array().of(
    Yup.object().shape(
      {
        id: Yup.string().required(),
        name: Yup.string().required(),
        email: emailSchema,
        phoneNumber: phoneNumberSchema,
        remindingTone: Yup.mixed<RemindingTone>().oneOf(Object.values(RemindingTone)).required(),
      },
      [['email', 'phoneNumber']],
    ),
  ),
});

const TRANS_NS = 'xml-upload';

export const XMLCompleteDataPage = () => {
  const { t } = useTranslation(TRANS_NS);
  const { xmlDataModel } = useXmlDataStore();
  const { updateXmlProcess } = useXmlProcessController();
  const [isLoading, setIsLoading] = useState(false);
  // ask user to confirm before leaving page since the data are not saved
  useBeforeUnload(true, 'Opuštěním stránky přijdete o data v paměti a proces nahrání xml budete muset začít znovu.');

  const { data } = useUserQuery();

  const { control, handleSubmit, getFieldState, formState } = useForm({
    resolver: yupResolver(customersValidationSchema),
    defaultValues: {
      customers: xmlDataModel.xmlCustomersWithoutContactInfo.map((c) => ({
        id: c.id,
        name: c.details.name ?? '',
        email: c.details.email ?? '',
        phoneNumber: c.details.phoneNumber ?? '',
        remindingTone: c.details.remindingTone ?? data?.user.defaultRemindingTone ?? RemindingTone.Neutral,
      })),
    },
  });

  const processFormData = async (data: FormValues) => {
    if (!data.customers?.length) {
      return false;
    }

    data.customers.forEach((c) => {
      // update customer in xml data store model
      xmlDataModel.updateCustomer(c.id, {
        name: c.name,
        email: c.email,
        phoneNumber: c.phoneNumber,
        remindingTone: c.remindingTone,
      });
    });

    // mark contact fill process as done
    xmlDataModel.contactsConfirmed = true;

    return true;
  };

  const onSubmit = async (data: FormValues) => {
    setIsLoading(true);

    await processFormData(data);

    setIsLoading(false);

    updateXmlProcess();
  };

  return (
    <PageLayout>
      <PageHeader
        title={t('title')}
        subtitle={t('completeData.title')}
        backRouteTo={'/upload/xml/summary'}
        Icon={ArrowBack}
      />
      <form onSubmit={handleSubmit((data) => onSubmit(data as FormValues))}>
        <PageContent
          sx={{
            flexDirection: 'column',
            gap: 4,
            alignItems: 'flex-start',
            justifyContent: 'flex-start',
          }}
        >
          {xmlDataModel.xmlCustomersWithoutContactInfo.map((c, index) => {
            const customerInvoices = xmlDataModel.getCustomerInvoices(c);
            const phoneNumberFieldState = getFieldState(`customers.${index}.phoneNumber`, formState);
            const emailFieldState = getFieldState(`customers.${index}.email`, formState);

            const shouldShowInvalidContactInfoWarning = phoneNumberFieldState.invalid || emailFieldState.invalid;

            return (
              <CustomerCompleteDataBox
                key={c.id}
                name={`customers[${index}]`}
                control={control}
                customer={{
                  id: c.id,
                  vatId: c.details.vatId,
                  taxId: c.details.taxId,
                  name: c.details.name,
                  address: c.details.address,
                  remindingTone: c.details.remindingTone ?? data?.user.defaultRemindingTone ?? RemindingTone.Neutral,
                }}
                invoicesSummary={{
                  count: customerInvoices.length,
                  totalAmount: fromCentesimal(
                    customerInvoices.reduce((acc, curr) => acc + (curr.details.amount ?? 0), 0),
                  ),
                  amountDue: fromCentesimal(
                    customerInvoices.reduce((acc, curr) => acc + (curr.details.amountDue ?? 0), 0),
                  ),
                  currency: customerInvoices[0]?.details.currency ?? 'other',
                }}
                shouldDisplayInvalidContactInfoProps={shouldShowInvalidContactInfoWarning}
              />
            );
          })}

          {!xmlDataModel.xmlCustomersWithoutContactInfo.length ? (
            <Box alignItems={'center'} justifyContent={'center'} display={'flex'} width={'100%'}>
              <Alert severity={'info'}>
                <Typography>{t('completeData.noCustomers')}</Typography>
              </Alert>
            </Box>
          ) : null}

          <Box display={'flex'} width={'100%'} justifyContent={'flex-end'}>
            <LoadingButton
              loading={isLoading}
              type={'submit'}
              size={'large'}
              variant={'contained'}
              endIcon={<ArrowForward />}
            >
              {t('continue')}
            </LoadingButton>
          </Box>
        </PageContent>
      </form>
    </PageLayout>
  );
};

type CustomerCompleteDataProps = {
  customer: Pick<Customer, 'name' | 'vatId' | 'taxId' | 'address' | 'email' | 'phoneNumber' | 'id' | 'remindingTone'>;
  invoicesSummary?: {
    count: number;
    totalAmount: number;
    amountDue: number;
    currency: string;
  };
  control: Control;
  name: string;
  shouldDisplayInvalidContactInfoProps?: boolean;
} & PaperProps;

const CustomerCompleteDataBox = ({
  customer,
  name,
  control,
  invoicesSummary,
  sx,
  shouldDisplayInvalidContactInfoProps,
  ...rest
}: CustomerCompleteDataProps) => {
  const { t } = useTranslation(TRANS_NS);
  return (
    <Paper sx={{ width: '100%', p: 4, ...sx }} {...rest}>
      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} flexWrap={'wrap'}>
        <Stack gap={0.5}>
          <FormInput
            sx={{ flex: 1, minWidth: 320 }}
            control={control}
            name={`${name}.name`}
            label={t('completeData.name') + '*'}
          />
          <Typography color={'textSecondary'} variant={'overline'}>{`IČ: ${
            customer.vatId ?? '-'
          } ・ DIČ: ${customer.taxId ?? '-'} ・ ${customer.address ?? '-'}`}</Typography>
        </Stack>
        {invoicesSummary ? (
          <Alert severity={'info'} variant={'outlined'} elevation={0}>
            <AlertTitle>
              {t('completeData.customerInvoicesSummary', {
                count: invoicesSummary.count,
                amount: getPriceWithCurrencyString(invoicesSummary.totalAmount, invoicesSummary.currency),
              })}
            </AlertTitle>
            <Typography>
              {t('completeData.leftToRemind', {
                amountDue: getPriceWithCurrencyString(invoicesSummary.amountDue, invoicesSummary.currency),
              })}
            </Typography>
          </Alert>
        ) : null}
      </Stack>
      {shouldDisplayInvalidContactInfoProps ? (
        <Alert sx={{ flex: 1, minWidth: 320, mt: { xs: 2, sm: 0 } }} severity={'error'}>
          {t('contactInvalidWarning', { ns: 'translation' })}
        </Alert>
      ) : null}
      <Stack gap={1} mt={2} direction={'row'} flexWrap={'wrap'}>
        <FormInput
          sx={{ flex: 1, minWidth: 320 }}
          control={control}
          name={`${name}.email`}
          label={t('completeData.email') + '*'}
        />
        <FormInput
          sx={{ flex: 1, minWidth: 320 }}
          control={control}
          name={`${name}.phoneNumber`}
          label={t('completeData.phoneNumber') + '*'}
        />
        <FormSelect
          sx={{ flex: 1, minWidth: 320 }}
          labelProps={{
            sx: {
              background: 'white',
            },
          }}
          control={control}
          name={`${name}.remindingTone`}
          label={t('remindingTone.title', { ns: 'translation' })}
          helperText={t('completeData.remindingTone.helperText') as string}
          options={Object.values(RemindingTone).map((tone) => ({
            label: t(getRemindingToneTransKey(tone), { ns: 'translation' }),
            value: tone,
          }))}
        />
        <input type={'hidden'} name={`${name}.id`} value={customer.id} />
      </Stack>
    </Paper>
  );
};
